/**
 * Doctor Registration with license verification and informed consent.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const specializations = [
  'General Practice', 'Internal Medicine', 'Cardiology', 'Endocrinology',
  'Nephrology', 'Oncology', 'Pulmonology', 'Neurology', 'Psychiatry',
  'Pediatrics', 'Dermatology', 'Ophthalmology', 'Orthopedics',
  'Obstetrics & Gynecology', 'Surgery', 'Family Medicine', 'HIV/AIDS Specialist',
  'Diabetology', 'Rheumatology', 'Other'
];

const kenyanCounties = [
  'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
  'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
  'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos',
  'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', 'Murang\'a',
  'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri', 'Samburu',
  'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans-Nzoia', 'Turkana',
  'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
];

const DoctorRegister = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone_number: '',
    license_number: '',
    specialization: '',
    services_offered: [],
    years_of_experience: '',
    county: '',
    hospital_affiliation: '',
    bio: '',
    consultation_fee: '',
    consent_given: false,
  });

  const { signup, login, registerProfile } = useAuth();
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services_offered: prev.services_offered.includes(service)
        ? prev.services_offered.filter(s => s !== service)
        : [...prev.services_offered, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_given) {
      toast.error('You must agree to the terms to register.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      let authUser;

      try {
        authUser = await signup(formData.email, formData.password, formData.full_name);
      } catch (signupErr) {
        if (signupErr.code === 'auth/email-already-in-use') {
          authUser = await login(formData.email, formData.password);
        } else {
          throw signupErr;
        }
      }

      // Normalize phone number to E.164 (Kenyan local 07xx → +25407xx)
      const rawPhone = (formData.phone_number || '').trim().replace(/[\s-]/g, '');
      const normalizedPhone = rawPhone.startsWith('+') ? rawPhone
        : rawPhone.startsWith('0') ? '+254' + rawPhone.slice(1)
        : rawPhone.startsWith('254') ? '+' + rawPhone
        : '+254' + rawPhone;

      // Build services_offered: always includes primary specialization
      const servicesOffered = formData.specialization
        ? [formData.specialization, ...formData.services_offered.filter(s => s !== formData.specialization)]
        : formData.services_offered;

      await registerProfile({
        email: formData.email,
        full_name: formData.full_name,
        phone_number: normalizedPhone,
        role: 'doctor',
        license_number: formData.license_number,
        specialization: formData.specialization,
        services_offered: servicesOffered,
        years_of_experience: parseInt(formData.years_of_experience) || 0,
        county: formData.county,
        hospital_affiliation: formData.hospital_affiliation,
        bio: formData.bio,
        consultation_fee: parseFloat(formData.consultation_fee) || 500,
        consent_given: true,
      }, authUser);

      toast.success('Registration submitted! Awaiting admin verification.');
      navigate('/doctor/pending');
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessages = {
        'auth/email-already-in-use': 'An account with this email already exists.',
        'auth/weak-password': 'Password is too weak.',
        'auth/invalid-email': 'Please enter a valid email address.',
      };
      toast.error(errorMessages[err.code] || err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="max-w-md mx-auto px-6 pt-8 pb-8">
        <Link to="/doctor/login" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Login
        </Link>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Doctor Registration</h1>
          <p className="text-gray-500 mt-1">Step {step} of 3</p>
          <div className="flex gap-2 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= step ? 'bg-accent-500' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Account */}
          {step === 1 && (
            <div className="card space-y-4">
              <h2 className="section-title">Account Information</h2>

              <div>
                <label className="label">Full Name *</label>
                <input type="text" value={formData.full_name} onChange={e => updateField('full_name', e.target.value)} className="input-field" placeholder="Dr. Jane Doe" required />
              </div>

              <div>
                <label className="label">Email Address *</label>
                <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="input-field" placeholder="doctor@example.com" required />
              </div>

              <div>
                <label className="label">Phone Number *</label>
                <input type="tel" value={formData.phone_number} onChange={e => updateField('phone_number', e.target.value)} className="input-field" placeholder="0712345678" required />
              </div>

              <div>
                <label className="label">Password *</label>
                <input type="password" value={formData.password} onChange={e => updateField('password', e.target.value)} className="input-field" placeholder="Min. 6 characters" required minLength={6} />
              </div>

              <div>
                <label className="label">Confirm Password *</label>
                <input type="password" value={formData.confirmPassword} onChange={e => updateField('confirmPassword', e.target.value)} className="input-field" placeholder="Re-enter password" required />
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!formData.full_name || !formData.email || !formData.password) {
                    toast.error('Please fill all required fields.');
                    return;
                  }
                  setStep(2);
                }}
                className="w-full py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 transition-colors"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Professional Info */}
          {step === 2 && (
            <div className="card space-y-4">
              <h2 className="section-title">Professional Information</h2>

              <div>
                <label className="label">KMPDB License Number *</label>
                <input type="text" value={formData.license_number} onChange={e => updateField('license_number', e.target.value)} className="input-field" placeholder="e.g., A12345" required />
                <p className="text-xs text-gray-400 mt-1">Kenya Medical Practitioners & Dentists Board license</p>
              </div>

              <div>
                <label className="label">Primary Specialization *</label>
                <select value={formData.specialization} onChange={e => updateField('specialization', e.target.value)} className="input-field" required>
                  <option value="">Select Specialization</option>
                  {specializations.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Additional services the doctor can offer */}
              <div>
                <label className="label">Additional Services Offered <span className="text-gray-400 font-normal">(optional)</span></label>
                <p className="text-xs text-gray-400 mb-2">Select any other specializations you can see patients for.</p>
                <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
                  {specializations.filter(s => s !== formData.specialization && s !== 'Other').map(s => (
                    <label key={s} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors ${
                      formData.services_offered.includes(s) ? 'bg-accent-50 border border-accent-300 text-accent-800' : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}>
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 rounded accent-accent-500"
                        checked={formData.services_offered.includes(s)}
                        onChange={() => toggleService(s)}
                      />
                      {s}
                    </label>
                  ))}
                </div>
                {formData.services_offered.length > 0 && (
                  <p className="text-xs text-accent-600 mt-1.5">{formData.services_offered.length} additional service{formData.services_offered.length > 1 ? 's' : ''} selected</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Years of Experience</label>
                  <input type="number" value={formData.years_of_experience} onChange={e => updateField('years_of_experience', e.target.value)} className="input-field" min="0" max="60" />
                </div>
                <div>
                  <label className="label">Consultation Fee (KES)</label>
                  <input type="number" value={formData.consultation_fee} onChange={e => updateField('consultation_fee', e.target.value)} className="input-field" placeholder="500" min="0" />
                </div>
              </div>

              <div>
                <label className="label">County</label>
                <select value={formData.county} onChange={e => updateField('county', e.target.value)} className="input-field">
                  <option value="">Select County</option>
                  {kenyanCounties.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Hospital Affiliation</label>
                <input type="text" value={formData.hospital_affiliation} onChange={e => updateField('hospital_affiliation', e.target.value)} className="input-field" placeholder="e.g., Kenyatta National Hospital" />
              </div>

              <div>
                <label className="label">Professional Bio</label>
                <textarea value={formData.bio} onChange={e => updateField('bio', e.target.value)} className="input-field" rows="3" placeholder="Brief professional background..." />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">Back</button>
                <button
                  type="button"
                  onClick={() => {
                    if (!formData.license_number || !formData.specialization) {
                      toast.error('License number and specialization are required.');
                      return;
                    }
                    setStep(3);
                  }}
                  className="flex-1 py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Consent */}
          {step === 3 && (
            <div className="card space-y-4">
              <h2 className="section-title">Terms & Verification</h2>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold text-green-800 mb-1">Verification Required</p>
                    <p>Your account will be reviewed by our admin team. We will verify your KMPDB license number before activating your account. This process typically takes 24-48 hours.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 max-h-48 overflow-y-auto">
                <h3 className="font-semibold mb-2">Doctor Terms of Service</h3>
                <ol className="list-decimal list-inside space-y-2">
                  <li>I confirm my medical license is valid and in good standing with the KMPDB.</li>
                  <li>I will provide professional medical consultations in accordance with Kenyan medical practice standards.</li>
                  <li>I will maintain patient confidentiality in compliance with the Kenya Data Protection Act, 2019.</li>
                  <li>I understand that my credentials will be verified before activation of my account.</li>
                  <li>I agree to the platform's consultation fees and payment processing terms.</li>
                  <li>I will accurately document all consultations, diagnoses, and prescriptions.</li>
                  <li>I can be held liable for negligent medical advice provided through this platform.</li>
                </ol>
              </div>

              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.consent_given}
                  onChange={e => updateField('consent_given', e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-accent-500 focus:ring-accent-400"
                />
                <span className="text-sm text-gray-700">
                  I confirm all information provided is accurate. I agree to the terms of service and understand the verification process.
                </span>
              </label>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">Back</button>
                <button
                  type="submit"
                  disabled={loading || !formData.consent_given}
                  className="flex-1 py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {loading && <div className="spinner w-5 h-5 border-2"></div>}
                  Submit Registration
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default DoctorRegister;
